// src/prototype/components/sep/SEPSectionGovernance.tsx
// Component 8: governance_framework

import React from 'react';
import { CheckCircle2, Calendar } from 'lucide-react';
import type { MappedGovernanceFramework } from '../../utils/mapStrategicExecutionPlan';

export interface SEPSectionGovernanceProps {
  clientName: string;
  governanceFramework: MappedGovernanceFramework | null;
}

export const SEPSectionGovernance: React.FC<SEPSectionGovernanceProps> = ({
  clientName, governanceFramework,
}) => {
  const approvalWorkflow = governanceFramework?.approvalWorkflow ?? [];
  const oversightCadence = governanceFramework?.oversightCadence ?? [];

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 8</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Governance & Approval Framework</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Executive approval workflow and ongoing oversight cadence</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        A structured governance model ensures that <strong>{clientName}</strong>'s initiative portfolio maintains executive visibility, financial accountability, and clinical alignment throughout execution. The framework below defines the approval workflow and ongoing oversight cadence.
      </p>

      <div className="mb-6">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">Approval Workflow</p>
        {approvalWorkflow.length > 0 ? (
          <div className="relative isolate">
            <div className="absolute left-[18px] top-8 bottom-8 w-[2px] bg-[#e5e7eb] z-0" aria-hidden />
            <div className="space-y-3">
              {approvalWorkflow.map(step => (
                <div key={step.step} className="relative flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 border-2 ${step.isFinal ? 'bg-[#FF6E4C] border-[#FF6E4C]' : 'bg-white border-[#18365E]'}`}>
                    {step.isFinal
                      ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <span className="text-xs font-bold text-[#18365E]">{step.step}</span>}
                  </div>
                  <div className="flex-1 bg-white border border-[#e5e7eb] rounded-xl p-3.5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-[#18365E]">{step.title}</p>
                        {step.isFinal && (
                          <p className="text-[10px] font-semibold text-[#FF6E4C] mt-0.5">Final Approval</p>
                        )}
                      </div>
                      {step.responsibleGroup && (
                        <span className="text-[10px] font-semibold text-[#717182] bg-[#F5F7FA] px-2 py-0.5 rounded-full border border-[#e5e7eb] shrink-0 whitespace-nowrap">
                          {step.responsibleGroup}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#717182] leading-relaxed mt-1">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Approval workflow data is not available.</p>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-3.5 h-3.5 text-[#717182]/60" />
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Oversight Cadence</p>
        </div>
        {oversightCadence.length > 0 ? (
          <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[#717182] w-24">Cadence</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Meeting Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Participants List</th>
                </tr>
              </thead>
              <tbody>
                {oversightCadence.map((item, i) => (
                  <tr key={`${item.meetingType}-${i}`} className={i < oversightCadence.length - 1 ? 'border-b border-[#e5e7eb]/60' : ''}>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-[#18365E]/[0.06] text-[#18365E] rounded-full border border-[#18365E]/10">
                        {item.frequency}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-medium text-[#18365E]">{item.meetingType}</td>
                    <td className="px-3 py-2.5 text-[#717182]">{item.participants}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[#717182]">Oversight cadence data is not available.</p>
        )}
      </div>
    </div>
  );
};

export default SEPSectionGovernance;
